import React, { useMemo, useState } from "react";

import {
  cn,
  FALLBACK_PUBLICATION_IMAGE,
} from "../utils/publicationsUtils";

function getImageFormat(width, height) {
  if (!width || !height) return "unknown";

  const ratio = width / height;

  if (ratio < 0.8) return "portrait";
  if (ratio > 1.25) return "landscape";
  return "square";
}

export default function PublicationAdaptiveImage({
  src,
  alt,
  className = "",
  imageClassName = "",
  onClick,
  zoomable = false,
}) {
  const image = src || FALLBACK_PUBLICATION_IMAGE;

  const [format, setFormat] = useState("unknown");

  const wrapperClassName = useMemo(() => {
    if (format === "portrait") {
      return cn(
        "mx-auto w-full max-w-[420px]",
        "h-[440px] sm:h-[500px]",
        "md:h-[520px] lg:h-[560px]"
      );
    }

    if (format === "square") {
      return cn(
        "mx-auto w-full max-w-[760px]",
        "h-[360px] sm:h-[430px]",
        "md:h-[500px] lg:h-[560px]"
      );
    }

    return cn(
      "w-full",
      "h-[280px] sm:h-[360px]",
      "md:h-[430px] lg:h-[500px]"
    );
  }, [format]);

  return (
    <div
      role={zoomable ? "button" : undefined}
      tabIndex={zoomable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!zoomable) return;

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        "group relative isolate overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-900",
        "transition duration-300",
        zoomable &&
          "cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-[#1277B9]/20",
        wrapperClassName,
        className
      )}
    >
      <img
        src={image}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-xl"
        onError={(event) => {
          event.currentTarget.src = FALLBACK_PUBLICATION_IMAGE;
        }}
      />

      <div className="absolute inset-0 bg-white/20 dark:bg-slate-950/20" />

      <img
        src={image}
        alt={alt}
        loading="lazy"
        className={cn(
          "relative z-10 h-full w-full object-contain p-3 transition duration-500 group-hover:scale-[1.015] sm:p-5",
          imageClassName
        )}
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          setFormat(getImageFormat(naturalWidth, naturalHeight));
        }}
        onError={(event) => {
          event.currentTarget.src = FALLBACK_PUBLICATION_IMAGE;
        }}
      />

      <div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
    </div>
  );
}